import React, { Suspense, lazy } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";
import useToken, { TokenProvider } from "./features/authentication/hooks/useToken.jsx";
import { maskedActive } from "./util/maskedDomain";
import { routeRows } from "./pages/marketplace/storefrontPages";

// A dynamic-import failure right after a redeploy means the previous build's
// hashed chunk no longer exists on the server. loadChunk (util/chunkRecovery)
// owns the shared recovery budget: overlay + auto-reload, then a visible
// error screen — never a blank page and never the router's default error text.
import { loadChunk } from "./util/chunkRecovery";
import { noteNavigation, noteLocation } from "./util/versionWatchdog";
import { RouteErrorScreen, AppLoading } from "./components/appStatus.jsx";

// Direct render pages (auth guards)
const Login = lazy(() => loadChunk(() => import("./pages/account/login.jsx")));
const EnvironmentLogin = lazy(() => loadChunk(() => import("./pages/account/environmentLogin.jsx")));
const ForgotPassword = lazy(() => loadChunk(() => import("./pages/account/forgotPassword.jsx")));
const ResetPassword = lazy(() => loadChunk(() => import("./pages/account/resetpassword.jsx")));

// Layouts
const Layout = lazy(() => loadChunk(() => import("./pages/layout.jsx")));
// Static, not lazy: it is three lines, and it is the storefront's first tier.
import StorefrontLayout from "./pages/storefrontLayout.jsx";
const AccountLayout = lazy(() => loadChunk(() => import("./pages/accountLayout.jsx")));
const CustomerLayout = lazy(() => loadChunk(() => import("./pages/customerLayout.jsx")));

/** Helper: wraps a dynamic import into a lazy route */
const lazyRoute = (importer) => async () => {
  noteNavigation(); // throttled "did a new build ship?" probe per navigation
  const mod = await loadChunk(importer);
  return { Component: mod.default ?? mod.Component };
};

// Like lazyRoute but picks a named export (used by the marketplace
// storefront, whose module exports one component per page).
const lazyNamedRoute = (importer, name) => async () => {
  noteNavigation();
  const mod = await loadChunk(importer);
  return { Component: mod[name] ?? mod.default };
};

// Storefront pages are routed from ONE registry on both the office host
// (/marketplace/:slug/<path>) and a store's own masked domain (/<path>).
// Each row is [path, named export, module]; `module` picks the chunk.
const STOREFRONT_MODULES = {
  storefront: () => import("./pages/marketplace/storefront.jsx"),
  enrollWizard: () => import("./pages/marketplace/enrollWizard.jsx"),
};
const STOREFRONT_ROUTES = routeRows();

const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    errorElement: <RouteErrorScreen />,
    children: [
      { index: true, lazy: lazyRoute(() => import("./pages/home/home.jsx")) },
      // Customers / Inventory
      { path: "customers", lazy: lazyRoute(() => import("./pages/customers/customers.jsx")) },
      { path: "customers/all", lazy: lazyRoute(() => import("./pages/customers/allCustomers.jsx")) },
      { path: "customers/active", lazy: lazyRoute(() => import("./pages/customers/activeUsers.jsx")) },
      { path: "customers/new-enrollments", lazy: lazyRoute(() => import("./pages/customers/newEnrollments.jsx")) },
      { path: "customers/commissions-paid", lazy: lazyRoute(() => import("./pages/customers/commissionsPaid.jsx")) },
      { path: "customers/revenue-orders", lazy: lazyRoute(() => import("./pages/customers/revenueOrders.jsx")) },
      { path: "customers/top-product-orders", lazy: lazyRoute(() => import("./pages/customers/topProductOrders.jsx")) },
      { path: "customers/new", lazy: lazyRoute(() => import("./pages/customers/newCustomer.jsx")) },

      { path: "inventory/stores", lazy: lazyRoute(() => import("./pages/inventory/stores.jsx")) },
      { path: "inventory/categories", lazy: lazyRoute(() => import("./pages/inventory/categories.jsx")) },
      { path: "inventory/products", lazy: lazyRoute(() => import("./pages/inventory/products.jsx")) },
      { path: "inventory/products/new", lazy: lazyRoute(() => import("./pages/inventory/newProduct.jsx")) },
      { path: "inventory/products/:productId/general", lazy: lazyRoute(() => import("./pages/inventory/productDetail.jsx")) },
      { path: "inventory/products/:productId/data", lazy: lazyRoute(() => import("./pages/inventory/productData.jsx")) },
      { path: "inventory/products/:productId/pricing", lazy: lazyRoute(() => import("./pages/inventory/productPricing.jsx")) },
      { path: "inventory/products/:productId/variants", lazy: lazyRoute(() => import("./pages/inventory/productVariants.jsx")) },
      { path: "inventory/products/:productId/images", lazy: lazyRoute(() => import("./pages/inventory/productImages.jsx")) },
      { path: "inventory/products/:productId/bom", lazy: lazyRoute(() => import("./pages/inventory/productBom.jsx")) },

      // Commissions
      { path: "commissions/periods", lazy: lazyRoute(() => import("./pages/commissions/periods.jsx")) },
      { path: "commissions/periods/:periodId/summary", lazy: lazyRoute(() => import("./pages/commissions/periodDetail.jsx")) },
      { path: "commissions/periods/:periodId/volumesummary", lazy: lazyRoute(() => import("./pages/commissions/volumesummary.jsx")) },
      { path: "commissions/periods/:periodId/bonusDetail/:bonusId", lazy: lazyRoute(() => import("./pages/commissions/bonusDetail.jsx")) },
      { path: "commissions/periods/:periodId/rankDetail/:rankId", lazy: lazyRoute(() => import("./pages/commissions/rankDetail.jsx")) },
      { path: "commissions/payables", lazy: lazyRoute(() => import("./pages/commissions/payables.jsx")) },
      { path: "commissions/payables2", lazy: lazyRoute(() => import("./pages/commissions/payables2.jsx")) },
      { path: "audiences", lazy: lazyRoute(() => import("./pages/audiences/audiences.jsx")) },
      { path: "customers360", lazy: lazyRoute(() => import("./pages/customers/customer360List.jsx")) },
      { path: "customers360/visitor/:visitorId", lazy: lazyRoute(() => import("./pages/customers/visitor360Page.jsx")) },
      { path: "pushnotifications", lazy: lazyRoute(() => import("./pages/push/pushNotifications.jsx")) },
      { path: "emailcampaigns", lazy: lazyRoute(() => import("./pages/email/emailCampaigns.jsx")) },
      { path: "commissions/paid", lazy: lazyRoute(() => import("./pages/commissions/paymentHistory.jsx")) },
      { path: "commissions/paid/:batchId", lazy: lazyRoute(() => import("./pages/commissions/paymentHistoryDetail.jsx")) },
      { path: "compensationPlans", lazy: lazyRoute(() => import("./pages/commissions/planList.jsx")) },
      { path: "compensationPlan/:planId", lazy: lazyRoute(() => import("./pages/commissions/planOverview.jsx")) },

      // Tools / Reports
      { path: "media", lazy: lazyRoute(() => import("./pages/tools/mediaList.jsx")) },
      { path: "media/categories", lazy: lazyRoute(() => import("./pages/tools/mediaCategories.jsx")) },
      { path: "schedule", lazy: lazyRoute(() => import("./pages/tools/schedule.jsx")) },
      { path: "training", lazy: lazyRoute(() => import("./pages/tools/training.jsx")) },
      { path: "training/progress", lazy: lazyRoute(() => import("./pages/tools/trainingProgress.jsx")) },
      { path: "training/:courseId", lazy: lazyRoute(() => import("./pages/tools/editCourse.jsx")) },

      { path: "reports", lazy: lazyRoute(() => import("./pages/reports/reports.jsx")) },
      { path: "reports/graphQL", lazy: lazyRoute(() => import("./pages/reports/reportQuery.jsx")) },
      { path: "reports/new", lazy: lazyRoute(() => import("./pages/reports/editReport.jsx")) },
      { path: "reports/:reportId", lazy: lazyRoute(() => import("./pages/reports/report.jsx")) },
      { path: "reports/:reportId/edit", lazy: lazyRoute(() => import("./pages/reports/editReport.jsx")) },

      { path: "query", lazy: lazyRoute(() => import("./pages/reports/reportQuery.jsx")) },
      { path: "tools/adjustments", lazy: lazyRoute(() => import("./pages/tools/adjustments.jsx")) },

      // Settings
      { path: "settings/users", lazy: lazyRoute(() => import("./pages/settings/users.jsx")) },
      { path: "settings/theme", lazy: lazyRoute(() => import("./pages/settings/theme.jsx")) },
      { path: "settings/navigation", lazy: lazyRoute(() => import("./pages/settings/navigation.jsx")) },
      { path: "settings/pages/:pageId/widgets/:widgetId", lazy: lazyRoute(() => import("./pages/settings/editWidget.jsx")) },
      { path: "settings/pages", lazy: lazyRoute(() => import("./pages/settings/pages.jsx")) },
      { path: "settings/backups", lazy: lazyRoute(() => import("./pages/settings/backups.jsx")) },
      { path: "settings/integrations", lazy: lazyRoute(() => import("./pages/settings/integrations.jsx")) },
      { path: "settings/mcp", lazy: lazyRoute(() => import("./pages/settings/mcp.jsx")) },
      { path: "mcp/authorize", lazy: lazyRoute(() => import("./pages/settings/mcpAuthorize.jsx")) },
      { path: "settings/pages/:pageId", lazy: lazyRoute(() => import("./pages/settings/pageSettings/customerDetailSettings.jsx")) },
      { path: "settings/pages/tree/:treeId", lazy: lazyRoute(() => import("./pages/settings/pageSettings/treeSettings.jsx")) },
      { path: "settings/widgets", lazy: lazyRoute(() => import("./pages/settings/widgets.jsx")) },
      { path: "settings/widgets/:widgetId", lazy: lazyRoute(() => import("./pages/settings/editWidget.jsx")) },
      { path: "email/metrics", lazy: lazyRoute(() => import("./pages/email/emailMetricDetail.jsx")) },
      { path: "settings/email/providers", lazy: lazyRoute(() => import("./pages/settings/emailSettings.jsx")) },
      { path: "settings/email/content", lazy: lazyRoute(() => import("./pages/settings/emailContent.jsx")) },
      { path: "settings/email/content/:templateId", lazy: lazyRoute(() => import("./pages/settings/emailContentEditor.jsx")) },
      // Old Settings campaigns: open the new page once they've moved there.
      { path: "settings/email/campaigns", lazy: lazyRoute(() => import("./pages/settings/oldCampaignsRoute.jsx")) },
      { path: "settings/email/campaigns/new", lazy: lazyRoute(() => import("./pages/settings/oldCampaignsRoute.jsx")) },
      { path: "settings/email/campaigns/:campaignId/edit", lazy: lazyRoute(() => import("./pages/settings/oldCampaignsRoute.jsx")) },
      { path: "settings/payments", lazy: lazyRoute(() => import("./pages/settings/payments.jsx")) },
      { path: "settings/regions", lazy: lazyRoute(() => import("./pages/settings/regions.jsx")) },
      { path: "settings/regions/:regionId", lazy: lazyRoute(() => import("./pages/settings/regionDetail.jsx")) },
      { path: "settings/countries", lazy: lazyRoute(() => import("./pages/settings/countries.jsx")) },
      { path: "settings/currency", lazy: lazyRoute(() => import("./pages/settings/currency.jsx")) },
      { path: "settings/salestax", lazy: lazyRoute(() => import("./pages/settings/salesTax.jsx")) },
      { path: "settings/salesTax", lazy: lazyRoute(() => import("./pages/settings/salesTax.jsx")) },
      { path: "settings/gateway", lazy: lazyRoute(() => import("./pages/settings/gateway.jsx")) },
      { path: "settings/shipping", lazy: lazyRoute(() => import("./pages/settings/shipping.jsx")) },
      { path: "settings/shippingzones", lazy: lazyRoute(() => import("./pages/settings/shippingZones.jsx")) },
      { path: "settings/coupons", lazy: lazyRoute(() => import("./pages/settings/coupons.jsx")) },
      { path: "settings/wizlo", lazy: lazyRoute(() => import("./pages/settings/wizlo.jsx")) },
      { path: "settings/myus", lazy: lazyRoute(() => import("./pages/settings/myus.jsx")) },
      { path: "settings/storeapis", lazy: lazyRoute(() => import("./pages/settings/storeApis.jsx")) },
      { path: "settings/points", lazy: lazyRoute(() => import("./pages/settings/points.jsx")) },
      { path: "settings/advanced", lazy: lazyRoute(() => import("./pages/settings/advanced.jsx")) },
      { path: "settings/push", lazy: lazyRoute(() => import("./pages/settings/pushSettings.jsx")) },
      { path: "marketplace", lazy: lazyRoute(() => import("./pages/marketplace/marketplaces.jsx")) },
      { path: "marketplace/manage/:marketplaceId", lazy: lazyRoute(() => import("./pages/marketplace/marketplaceDetail.jsx")) },
      { path: "marketplace/subscriptions", lazy: lazyRoute(() => import("./pages/marketplace/subscriptions.jsx")) },
      // NOTE: nested under orders/ on purpose — the PUBLIC_STOREFRONT_PATH
      // regex below excludes marketplace/orders(/...), so this stays a
      // corporate page instead of being treated as a public storefront slug.
      { path: "marketplace/orders/new", lazy: lazyRoute(() => import("./pages/marketplace/createOrderWizard.jsx")) },
      { path: "marketplace/orders", lazy: lazyRoute(() => import("./pages/marketplace/marketplaceOrders.jsx")) },
      { path: "marketplace/points", lazy: lazyRoute(() => import("./pages/marketplace/points.jsx")) },
      { path: "marketplace/form-collection", lazy: lazyRoute(() => import("./pages/marketplace/formCollection.jsx")) },
      { path: "marketplace/preview/:templateId", lazy: lazyRoute(() => import("./pages/marketplace/templatePreview.jsx")) },
      { path: "marketplace/:slug", lazy: lazyNamedRoute(() => import("./pages/marketplace/storefront.jsx"), "StorefrontLanding") },
      // Every other storefront page comes from the page registry
      // (pages/marketplace/storefrontPages.json) — the SAME table the masked
      // router, the SEO head and the API prerender use. Add a page there +
      // its named export in storefront.jsx; never here.
      ...STOREFRONT_ROUTES.map(([path, name, mod]) => ({
        path: `marketplace/:slug/${path}`,
        lazy: lazyNamedRoute(STOREFRONT_MODULES[mod], name),
      })),
      // Referral links: static segments above always win, so any reserved
      // page name beats a webalias (and the API refuses reserved aliases).
      { path: "marketplace/:slug/:webalias", lazy: lazyNamedRoute(() => import("./pages/marketplace/storefront.jsx"), "StorefrontRefCapture") },
      // Any deeper store path no page claimed: "<page>/<referral handle>"
      // captures the sponsor and lands on that page; anything else renders the
      // store-branded "page doesn't exist" screen (never the office 404).
      { path: "marketplace/:slug/*", lazy: lazyNamedRoute(() => import("./pages/marketplace/storefront.jsx"), "StorefrontRefCapture") },
      { path: "settings/statuses", lazy: lazyRoute(() => import("./pages/settings/statuses.jsx")) },
      { path: "settings/company", lazy: lazyRoute(() => import("./pages/settings/company.jsx")) },
      { path: "settings/newdeal", lazy: lazyRoute(() => import("./pages/settings/newDeal.jsx")) },
      { path: "settings/trees", lazy: lazyRoute(() => import("./pages/settings/placementRules.jsx")) },
      { path: "settings/orderstatuses", lazy: lazyRoute(() => import("./pages/settings/orderStatuses.jsx")) },
      { path: "profile", lazy: lazyRoute(() => import("./pages/account/myProfile.jsx")) },
      // Plan templates live under Manage Account (corporate-only shell); the old
      // /templates/... URLs redirect there so bookmarks keep working.
      { path: "templates", element: <Navigate to="/account/compensationplans" replace /> },
      { path: "templates/:id/detail", element: <TemplatesRedirect sub="detail" /> },
      { path: "templates/:id/edit", element: <TemplatesRedirect sub="edit" /> },

      // SSO
      { path: "sso/oauth-bridge", lazy: lazyRoute(() => import("./pages/sso/oauthBridge.jsx")) },

      // Other
      { path: "page/:pageId", lazy: lazyRoute(() => import("./pages/corporatePage.jsx")) },
      { path: "*", lazy: lazyRoute(() => import("./pages/nopage.jsx")) },
    ],
  },
  {
    path: "/account",
    element: <AccountLayout />,
    errorElement: <RouteErrorScreen />,
    children: [
      { index: true, lazy: lazyRoute(() => import("./pages/account/environments/environmentList.jsx")) },
      { path: "profile", lazy: lazyRoute(() => import("./pages/account/profile.jsx")) },
      { path: "environments", lazy: lazyRoute(() => import("./pages/account/environments/environmentList.jsx")) },
      { path: "environments/:id", lazy: lazyRoute(() => import("./pages/account/environments/environementDetail.jsx")) },
      { path: "environments/:environmentId/webhooklogs/:subscriptionId", lazy: lazyRoute(() => import("./pages/account/environments/webHookLogs.jsx")) },
      { path: "invoices", lazy: lazyRoute(() => import("./pages/account/invoices.jsx")) },
      { path: "subscription", lazy: lazyRoute(() => import("./pages/account/subscription.jsx")) },
      { path: "compensationplans", lazy: lazyRoute(() => import("./pages/account/compensationPlans.jsx")) },
      { path: "compensationplans/:id/detail", lazy: lazyRoute(() => import("./pages/account/compensationPlan.jsx")) },
      { path: "compensationplans/:id/edit", lazy: lazyRoute(() => import("./pages/account/templateEdit.jsx")) },
      { path: "users", lazy: lazyRoute(() => import("./pages/account/users.jsx")) },
      { path: "roles", lazy: lazyRoute(() => import("./pages/account/roles.jsx")) },
      { path: "logs", lazy: lazyRoute(() => import("./pages/account/logs.jsx")) },
      { path: "branding", lazy: lazyRoute(() => import("./pages/account/branding.jsx")) },
      { path: "ai", lazy: lazyRoute(() => import("./pages/account/aiAgents.jsx")) },
    ],
  },

  // Customer routes
  {
    path: "/customers/:customerId",
    element: <CustomerLayout />,
    errorElement: <RouteErrorScreen />,
    children: [
      { path: "team", lazy: lazyRoute(() => import("./pages/customers/teamList.jsx")) },
      { path: "edit", lazy: lazyRoute(() => import("./pages/customers/editCustomer.jsx")) },
      { path: "summary", lazy: lazyRoute(() => import("./pages/customers/summary.jsx")) },
      { path: "details", lazy: lazyRoute(() => import("./pages/customers/customerDetail.jsx")) },
      { path: "dashboard", lazy: lazyRoute(() => import("./pages/customers/dashboard.jsx")) },
      { path: "orders", lazy: lazyRoute(() => import("./pages/customers/customerOrders.jsx")) },
      { path: "autoships", lazy: lazyRoute(() => import("./pages/customers/autoships.jsx")) },
      { path: "orders/:orderId", lazy: lazyRoute(() => import("./pages/customers/orderDetail.jsx")) },
      { path: "orders/:orderId/edit", lazy: lazyRoute(() => import("./pages/customers/editOrder.jsx")) },
      { path: "shop", lazy: lazyRoute(() => import("./pages/customers/shop.jsx")) },
      { path: "checkout", lazy: lazyRoute(() => import("./pages/customers/checkout.jsx")) },
      { path: "training", lazy: lazyRoute(() => import("./pages/tools/training.jsx")) },
      { path: "training/:courseId", lazy: lazyRoute(() => import("./pages/customers/course.jsx")) },
      { path: "account/profile", lazy: lazyRoute(() => import("./pages/customers/account/profile.jsx")) },
      { path: "account/security", lazy: lazyRoute(() => import("./pages/customers/account/security.jsx")) },
      { path: "account/moneyin", lazy: lazyRoute(() => import("./pages/customers/account/moneyIn.jsx")) },
      { path: "account/moneyout", lazy: lazyRoute(() => import("./pages/customers/account/moneyOut.jsx")) },
      { path: "account/treesettings", lazy: lazyRoute(() => import("./pages/customers/account/treeSettings.jsx")) },
      { path: "account/withdrawmethod", lazy: lazyRoute(() => import("./pages/customers/account/withdrawMethod.jsx")) },
      { path: "account/affiliate", lazy: lazyRoute(() => import("./pages/customers/account/becomeAffiliate.jsx")) },
      { path: "account/member", lazy: lazyRoute(() => import("./pages/customers/account/becomeMember.jsx")) },
      { path: "commissions", lazy: lazyRoute(() => import("./pages/customers/commissionsDetail.jsx")) },
      { path: "commissions/:bonusId", lazy: lazyRoute(() => import("./pages/customers/commissionsBonusDetail.jsx")) },
      { path: "tree/:treeId", lazy: lazyRoute(() => import("./pages/customers/customerTree.jsx")) },
      { path: "placements", lazy: lazyRoute(() => import("./pages/customers/placementSuite.jsx")) },
      { path: "reports", lazy: lazyRoute(() => import("./pages/reports/reports.jsx")) },
      { path: "reports/:reportId", lazy: lazyRoute(() => import("./pages/reports/report.jsx")) },
      { path: "media", lazy: lazyRoute(() => import("./pages/tools/mediaList.jsx")) },
      { path: ":pageId", lazy: lazyRoute(() => import("./pages/customers/customerPage.jsx")) },
    ],
  },
]);

// ── Masked custom domains ────────────────────────────────────────────────────
// When the SPA is served on a store's own domain (aregomd.com) instead of
// office.aregobrands.com, the storefront lives at clean ROOT paths (/hrt,
// /shop, /product/:id) rather than /marketplace/:slug/... The slug is resolved
// from the host (see util/maskedDomain + main.jsx) and read by the storefront
// via getMaskedSlug() when there's no :slug route param.
const SF = STOREFRONT_MODULES.storefront;

// Old absolute /marketplace/:slug/... links clicked on a masked host are
// rewritten to their clean root form so the URL bar never leaks the prefix.
// eslint-disable-next-line react/prop-types
function TemplatesRedirect({ sub }) {
  const { id } = useParams();
  return <Navigate to={`/account/compensationplans/${id}/${sub}`} replace />;
}

function MaskedPrefixRedirect() {
  const loc = useLocation();
  const stripped = loc.pathname.replace(/^\/marketplace\/[^/]+/, "") || "/";
  return <Navigate to={stripped + loc.search + loc.hash} replace />;
}

const maskedRouter = createBrowserRouter([
  {
    path: "/",
    element: <StorefrontLayout />,
    errorElement: <RouteErrorScreen />,
    children: [
      { index: true, lazy: lazyNamedRoute(SF, "StorefrontLanding") },
      // The registry table above, minus the /marketplace/:slug prefix.
      ...STOREFRONT_ROUTES.map(([path, name, mod]) => ({
        path,
        lazy: lazyNamedRoute(STOREFRONT_MODULES[mod], name),
      })),
      { path: "marketplace/:slug/*", element: <MaskedPrefixRedirect /> },
      { path: "marketplace/:slug", element: <MaskedPrefixRedirect /> },
      // Sponsor referral webalias — single-segment dynamic, after every page.
      { path: ":webalias", lazy: lazyNamedRoute(SF, "StorefrontRefCapture") },
      // Everything else, MUST be last: "<page>/<handle>" referral links, or
      // the store's designed 404. Without it an unmatched path threw into the
      // "Something went wrong… after an update" screen.
      { path: "*", lazy: lazyNamedRoute(SF, "StorefrontRefCapture") },
    ],
  },
]);

// Every committed page change, in whichever router is on screen, is a moment
// to move this tab onto a newer release (util/versionWatchdog). One
// subscription here covers every layout, the first render after switching
// layouts included — the per-layout gates it replaces missed both.
for (const r of [router, maskedRouter]) {
  r.subscribe((state) => noteLocation(state.location.pathname));
}

// Customer-facing marketplace storefront paths — /marketplace/{slug}[/shop|
// /product/:id|/second-page|/portal/:id|/checkout[/:id]|/thankyou] — but NOT
// the corporate management pages (/marketplace, /marketplace/manage/...,
// /marketplace/subscriptions, /marketplace/orders, /marketplace/points,
// /marketplace/form-collection, /marketplace/preview/...). Same exclusion set layout.jsx's hideSidebar
// check already uses. A guest visiting one of these must reach the router
// (and the storefront's own public/authenticated API fallback) instead of
// being bounced to corporate login — that bounce is exactly what made
// "Public Storefront" unreachable for anonymous visitors even after the
// API/UI work was done.
const PUBLIC_STOREFRONT_PATH = /^\/marketplace\/(?!manage(\/|$))(?!subscriptions(\/|$))(?!orders(\/|$))(?!points(\/|$))(?!form-collection(\/|$))(?!preview(\/|$))[^/]+/;

function App() {
  const { token, setToken, clearToken } = useToken();
  const path = window.location.pathname.toLowerCase();
  const isEnvironmentDetailPath = /^\/account(?:\/|$)/.test(path);

  // Masked custom domain: the entire app is this store's storefront, rendered
  // at root paths and guest-accessible (no corporate login bounce).
  if (maskedActive()) {
    return (
      <TokenProvider clearToken={clearToken}>
        <RouterProvider router={maskedRouter} fallbackElement={<AppLoading />} />
      </TokenProvider>
    );
  }

  // Auth redirects handled before router
  if (path === "/account/forgotpassword") {
    return (
      <Suspense fallback={<AppLoading />}>
        <ForgotPassword />
      </Suspense>
    );
  }
  if (path === "/account/resetpassword") {
    return (
      <Suspense fallback={<AppLoading />}>
        <ResetPassword />
      </Suspense>
    );
  }

  if (!token) {
    if (PUBLIC_STOREFRONT_PATH.test(path)) {
      return (
        <TokenProvider clearToken={clearToken}>
          <RouterProvider router={router} fallbackElement={<AppLoading />} />
        </TokenProvider>
      );
    }
    return (
      <Suspense fallback={<AppLoading />}>
        <Login setToken={setToken} />
      </Suspense>
    );
  }
  if (!token.environmentId) {
    if (isEnvironmentDetailPath) {
      return (
        <TokenProvider clearToken={clearToken}>
          <RouterProvider router={router} fallbackElement={<AppLoading />} />
        </TokenProvider>
      );
    }
    return (
      <Suspense fallback={<AppLoading />}>
        <EnvironmentLogin setToken={setToken} clearToken={clearToken} />
      </Suspense>
    );
  }

  return (
    <TokenProvider clearToken={clearToken}>
      <RouterProvider router={router} fallbackElement={<AppLoading />} />
    </TokenProvider>
  );
}

export default App;
